import React, { useState, useEffect } from 'react';
import { getAlarms, acknowledgeAlarm } from '../../services/bobAssistantService';

const AlarmsList = ({ deviceId, onAlarmAcknowledged }) => {
    const [alarms, setAlarms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acknowledging, setAcknowledging] = useState(null);
    const [filter, setFilter] = useState('all'); // all, pending, acknowledged

    useEffect(() => {
        loadAlarms();
    }, [deviceId, filter]);

    const loadAlarms = async () => {
        try {
            setLoading(true);
            const params = { days: 90, limit: 50 };
            if (filter === 'pending') params.acknowledged = 'false';
            if (filter === 'acknowledged') params.acknowledged = 'true';

            const data = await getAlarms(deviceId, params);
            setAlarms(data);
        } catch (err) {
            console.error('Error loading alarms:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAcknowledge = async (alarmId) => {
        try {
            setAcknowledging(alarmId);
            await acknowledgeAlarm(deviceId, alarmId, '');
            await loadAlarms();
            if (onAlarmAcknowledged) onAlarmAcknowledged();
        } catch (err) {
            console.error('Error acknowledging alarm:', err);
        } finally {
            setAcknowledging(null);
        }
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString('en', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getAnomalyColor = (level) => {
        if (level >= 80) return '#F44336';
        if (level >= 50) return '#FF9800';
        if (level >= 25) return '#FFEB3B';
        return '#4CAF50';
    };

    return (
        <div className="bob-alarms-list">
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
            }}>
                <h4 style={{ margin: 0 }}>Alarm History</h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={() => setFilter('all')}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: filter === 'all' ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setFilter('pending')}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: filter === 'pending' ? 'rgba(244, 67, 54, 0.3)' : 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                    >
                        Pending
                    </button>
                    <button
                        onClick={() => setFilter('acknowledged')}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: filter === 'acknowledged' ? 'rgba(76, 175, 80, 0.3)' : 'transparent',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                    >
                        Acknowledged
                    </button>
                </div>
            </div>

            {loading ? (
                <p>Loading alarms...</p>
            ) : alarms.length === 0 ? (
                <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', padding: '40px' }}>
                    No alarms to display
                </p>
            ) : (
                <div>
                    {alarms.map((alarm) => (
                        <div
                            key={alarm.id}
                            className={`bob-alarm-item ${alarm.acknowledged ? 'acknowledged' : 'unacknowledged'}`}
                        >
                            <div className="bob-alarm-info">
                                <span
                                    className="bob-alarm-level"
                                    style={{ color: getAnomalyColor(alarm.anomaly_level) }}
                                >
                                    Anomaly: {alarm.anomaly_level}%
                                </span>
                                <span className="bob-alarm-time">
                                    {formatDate(alarm.timestamp)}
                                </span>
                                <div style={{
                                    fontSize: '13px',
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    marginTop: '4px'
                                }}>
                                    Temp: {alarm.temperature?.toFixed(1)}°C |
                                    Vibration: {alarm.vibration_level?.toFixed(2)}
                                </div>
                                {alarm.acknowledged && alarm.acknowledged_by_username && (
                                    <div style={{
                                        fontSize: '12px',
                                        color: 'rgba(76, 175, 80, 0.8)',
                                        marginTop: '4px'
                                    }}>
                                        ✓ Acknowledged by {alarm.acknowledged_by_username}
                                    </div>
                                )}
                            </div>
                            <div className="bob-alarm-actions">
                                {!alarm.acknowledged && (
                                    <button
                                        onClick={() => handleAcknowledge(alarm.id)}
                                        disabled={acknowledging === alarm.id}
                                    >
                                        {acknowledging === alarm.id ? 'Processing...' : 'Acknowledge'}
                                    </button>
                                )}
                                {alarm.fft_data && (
                                    <span style={{
                                        padding: '4px 8px',
                                        backgroundColor: 'rgba(99, 102, 241, 0.2)',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        marginLeft: '8px'
                                    }}>
                                        FFT available
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AlarmsList;
